/* eslint-disable */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const PreStartChecklist: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [project, setProject] = useState<any>(null);
  const [checklist, setChecklist] = useState<any[]>([]);

  const defaultChecklistItems = [
    'Agreement signed',
    'Bank reconfirmed',
    'Reporting cadence confirmed',
    'Funding conditions noted', // Changed from 'Tranche conditions noted'
    'Kickoff meeting scheduled'
  ];

  useEffect(() => {
    if (projectId) {
      loadProject();
      loadChecklist();
    }
  }, [projectId]);

  const loadProject = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .maybeSingle();

    if (error) {
      console.error('Error loading project:', error);
      setError(error.message || 'Failed to load project');
    }

    if (data) {
      setProject(data);
    } else {
      setError('Project not found');
    }
  };

  const loadChecklist = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('project_checklists')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true });

    if (!error && data && data.length > 0) {
      setChecklist(data);
    } else {
      // Initialize with default items
      const initialItems = defaultChecklistItems.map((item, index) => ({
        id: `temp-${index}`,
        project_id: projectId,
        checklist_item: item,
        is_checked: false
      }));
      setChecklist(initialItems);
    }
  };

  const handleToggleCheck = async (itemId: string, currentState: boolean) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('You must be logged in to update checklist items');
      return;
    }

    const item = checklist.find((c: any) => c.id === itemId);
    if (!item) return;

    // Optimistically update UI
    const newCheckedState = !currentState;
    setChecklist(prev => prev.map((c: any) =>
      c.id === itemId ? {
        ...c,
        is_checked: newCheckedState,
        checked_by: newCheckedState ? user.id : null,
        checked_at: newCheckedState ? new Date().toISOString() : null
      } : c
    ));

    try {
      if (itemId.startsWith('temp-')) {
        // Create new checklist item
        const { data: newItem, error } = await supabase
          .from('project_checklists')
          .insert({
            project_id: projectId,
            checklist_item: item.checklist_item,
            is_checked: newCheckedState,
            checked_by: newCheckedState ? user.id : null,
            checked_at: newCheckedState ? new Date().toISOString() : null
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating checklist item:', error);
          setError(error.message || 'Failed to update checklist item');
          // Revert optimistic update
          setChecklist(prev => prev.map((c: any) =>
            c.id === itemId ? { ...c, is_checked: currentState } : c
          ));
        } else if (newItem) {
          setChecklist(prev => prev.map((c: any) =>
            c.id === itemId ? newItem : c
          ));
        }
      } else {
        // Update existing item
        const { error } = await supabase
          .from('project_checklists')
          .update({
            is_checked: newCheckedState,
            checked_by: newCheckedState ? user.id : null,
            checked_at: newCheckedState ? new Date().toISOString() : null
          })
          .eq('id', itemId);

        if (error) {
          console.error('Error updating checklist item:', error);
          setError(error.message || 'Failed to update checklist item');
          // Revert optimistic update
          setChecklist(prev => prev.map((c: any) =>
            c.id === itemId ? { ...c, is_checked: currentState } : c
          ));
        }
      }
    } catch (err: any) {
      console.error('Unexpected error:', err);
      setError(err.message || 'An unexpected error occurred');
      // Revert optimistic update
      setChecklist(prev => prev.map((c: any) =>
        c.id === itemId ? { ...c, is_checked: currentState } : c
      ));
    }
  };

  const handleStartProject = async () => {
    const allChecked = checklist.every((item: any) => item.is_checked);
    if (!allChecked) {
      setError('Please complete all checklist items before starting the project');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('projects')
        .update({ status: 'active' })
        .eq('id', projectId);

      if (updateError) throw updateError;

      // Notify assigned NGOs that the project is now active
      const { data: assignedNGOs } = await supabase
        .from('project_ngos')
        .select('ngo_id')
        .eq('project_id', projectId);

      if (assignedNGOs && assignedNGOs.length > 0) {
        // Update project_ngos to trigger notification system
        // The notification system watches for project status changes
        for (const assignment of assignedNGOs) {
          await supabase
            .from('project_ngos')
            .update({
              // Update to trigger realtime notification
              status: 'active', // Ensure status stays in sync with project
              accepted_at: new Date().toISOString()
            })
            .eq('project_id', projectId)
            .eq('ngo_id', assignment.ngo_id);
        }
      }

      // TODO: Create calendar events (Step 14)
      // - Kickoff meeting
      // - KPI due dates (recurring)
      // - Milestone dates
      // - Funding dates
      // - UC/SoE due dates

      navigate(`/client/company/projects/${projectId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to start project');
    } finally {
      setLoading(false);
    }
  };

  if (!project) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (project.status !== 'pre_start') {
    return (
      <div className="form-container">
        <div className="form-section">
          <h1>Project Status</h1>
          <div className="card">
            <p>
              This project is currently {project.status}. The pre-start checklist is only available for projects in "pre_start" status.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/client/company/projects/${projectId}`)}
            >
              View Project
            </button>
          </div>
        </div>
      </div>
    );
  }

  const allChecked = checklist.every((item: any) => item.is_checked);

  return (
    <div className="form-container">
      <div className="form-section">
        <h1>Pre-Start Checklist</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
          Complete all items before starting the project: <strong>{project.name}</strong>
        </p>

        {error && (
          <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ margin: 0 }}>Checklist Items</h2>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.5rem 1rem',
              background: allChecked ? '#d1fae5' : '#fef3c7',
              borderRadius: '8px',
              border: `1px solid ${allChecked ? '#10b981' : '#f59e0b'}`
            }}>
              <span style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: allChecked ? '#065f46' : '#92400e'
              }}>
                {checklist.filter((item: any) => item.is_checked).length} / {checklist.length} Completed
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {checklist.map((item: any) => (
              <div
                key={item.id}
                onClick={() => handleToggleCheck(item.id, item.is_checked)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  padding: '1.25rem',
                  borderRadius: '12px',
                  border: `2px solid ${item.is_checked ? '#10b981' : '#e5e7eb'}`,
                  background: item.is_checked ? '#f0fdf4' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!item.is_checked) {
                    e.currentTarget.style.borderColor = '#10b981';
                    e.currentTarget.style.background = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!item.is_checked) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.background = 'white';
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={item.is_checked}
                  onChange={() => handleToggleCheck(item.id, item.is_checked)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                    marginTop: '2px',
                    flexShrink: 0,
                    accentColor: '#10b981'
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: item.is_checked && item.checked_at ? '0.5rem' : '0'
                  }}>
                    <strong style={{
                      fontSize: '1rem',
                      color: item.is_checked ? '#065f46' : '#111827',
                      textDecoration: item.is_checked ? 'line-through' : 'none',
                      opacity: item.is_checked ? 0.7 : 1
                    }}>
                      {item.checklist_item}
                    </strong>
                    {item.is_checked && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.75rem',
                        color: '#10b981',
                        fontWeight: 600,
                        background: '#d1fae5',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '6px'
                      }}>
                        ✓ Completed
                      </span>
                    )}
                  </div>
                  {item.is_checked && item.checked_at && (
                    <p style={{
                      margin: 0,
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      fontStyle: 'italic'
                    }}>
                      Completed on {new Date(item.checked_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate(`/client/company/projects/${projectId}`)}
          >
            Back to Project
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleStartProject}
            disabled={loading || !allChecked}
          >
            {loading ? 'Starting...' : 'Start Project'}
          </button>
        </div>

        {!allChecked && (
          <div className="toast toast-warning" style={{ position: 'relative', marginTop: '1rem' }}>
            Please complete all checklist items to start the project
          </div>
        )}
      </div>
    </div>
  );
};

export default PreStartChecklist;
